/**
 * useNutritionPlan — loads and generates the user's nutrition plan.
 * The plan is generated by the generate-nutrition-plan Edge Function.
 * It calls the Cerveau (Gemini) with nutrition_profile + athlete profile.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export interface MealFood {
  item: string;
  quantity: number;
  unit: string;
  calories: number;
}

export interface Meal {
  time: string;
  name: string;
  foods: MealFood[];
  total_calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
}

export interface DayPlan {
  day: string;
  meals: Meal[];
}

export interface NutritionPlanContent {
  calories_target: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  days: DayPlan[];
  notes: string;
  warnings: string[];
}

export function useNutritionPlan() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [generating, setGenerating] = useState(false);

  const { data: plan, isLoading } = useQuery({
    queryKey: ["nutrition-plan", user?.id],
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("nutrition_plans")
        .select("id, status, content_json, generated_at, expires_at")
        .eq("user_id", user!.id)
        .maybeSingle();
      return data ?? null;
    },
  });

  const hasPlan = plan?.status === "ready";
  const isGenerating = plan?.status === "generating" || generating;
  const hasError = plan?.status === "error";

  const isExpired = plan?.expires_at
    ? new Date(plan.expires_at) < new Date()
    : false;

  const generatePlan = useCallback(async () => {
    if (!user || generating) return;
    setGenerating(true);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Tu dois être connecté");
        return;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-nutrition-plan`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 429) {
          toast.error(data.error ?? "Le Cerveau est surchargé, réessaie plus tard.");
        } else if (res.status === 400) {
          toast.error(data.error ?? "Complète ton profil nutrition d'abord.");
        } else {
          toast.error(data.error ?? "Le Cerveau est temporairement indisponible.");
        }
        return;
      }

      toast.success("✅ Ton planning alimentaire est prêt !");
      qc.invalidateQueries({ queryKey: ["nutrition-plan"] });
    } catch {
      toast.error("Erreur de connexion au Cerveau.");
    } finally {
      setGenerating(false);
    }
  }, [user, generating, qc]);

  const planContent = plan?.content_json as NutritionPlanContent | null;

  return {
    plan: planContent,
    status: plan?.status ?? null,
    hasPlan,
    isGenerating,
    hasError,
    isExpired,
    loading: isLoading,
    generatedAt: plan?.generated_at ?? null,
    generatePlan,
  };
}
